import { z } from 'zod';
import { AcademyImageSchema } from './image';
import { BADGE_LABEL_KEYS } from '~/utils/academy/trainings/doceboTrainingBadges';

export type TeaserColor = 'blue' | 'pink' | 'orange';

export type TrainingStatusBadgeType = 'notStarted' | 'inProgress' | 'completed';

export const TRAINING_STATUS_LABEL_KEYS = {
    notStarted: 'academy.trainingStatus.notStarted',
    inProgress: 'academy.trainingStatus.inProgress',
    completed: 'academy.trainingStatus.completed'
} as const;

export type TrainingStatusLabelKey = (typeof TRAINING_STATUS_LABEL_KEYS)[keyof typeof TRAINING_STATUS_LABEL_KEYS];

export type BadgeType =
    | 'eLearning'
    | 'inPerson'
    | 'liveWebinar'
    | 'cme'
    | TrainingStatusBadgeType
    | 'new'
    | 'prize'
    | 'reminder'
    | 'certificate';

export const DoceboCourseStatusSchema = z.enum([
    'non_subscribed',
    'subscribed',
    'in_progress',
    'completed',
    'subscription_to_confirm',
    'waiting',
    'suspended'
]);

export type DoceboCourseStatus = z.infer<typeof DoceboCourseStatusSchema>;

export const AcademyBadgeSchema = z.object({
    type: z.enum([
        'eLearning',
        'inPerson',
        'liveWebinar',
        'cme',
        'notStarted',
        'inProgress',
        'completed',
        'new',
        'prize',
        'reminder',
        'certificate'
    ]),
    labelKey: z.enum([...Object.values(BADGE_LABEL_KEYS), ...Object.values(TRAINING_STATUS_LABEL_KEYS)]).optional(),
    icon: z.string().optional(),
    displayLabel: z.string().optional()
});

export const AdditionalFieldSchema = {
    trainingType: z.string().nullable().optional(),
    bonusPoints: z.string().nullable().optional(),
    learningFormat: z.string().nullable().optional(),
    product: z.string().nullable().optional()
};

export const BaseTrainingSchema = z.object({
    type: z.enum(['course', 'event']).optional(),
    id: z.string().optional(),
    courseId: z.number().int().optional(),
    title: z.string().optional(),
    shortDescription: z.string().optional(),
    description: z.string().optional(),
    image: AcademyImageSchema.optional(),
    badges: z.array(AcademyBadgeSchema).optional(),
    createdAt: z.string().optional(),
    color: z.enum(['blue', 'pink', 'orange']).optional(),
    ctaUrl: z.string().optional(),
    playUrl: z.string().optional(),
    certificateUrl: z.string().nullable().optional(),
    hasCertificate: z.boolean().optional(),
    points: z.number().int().nonnegative().nullable().optional(),
    status: DoceboCourseStatusSchema.nullable().optional(),
    durationMinutes: z.number().int().nonnegative().nullable().optional(),
    startsAt: z.string().optional(),
    endsAt: z.string().optional(),
    sameDay: z.boolean().optional(),
    location: z.string().optional(),
    ...AdditionalFieldSchema
});

export const TrainingListingEventSchema = BaseTrainingSchema.extend({
    type: z.literal('event'),
    id: z.string(),
    title: z.string(),
    image: AcademyImageSchema,
    badges: z.array(AcademyBadgeSchema),
    createdAt: z.string(),
    ctaUrl: z.string(),
    startsAt: z.string().meta({ description: 'Example: `2026-01-01 00:00`. This is in UTC' }),
    location: z.string()
});

export const TrainingCourseDetailSchema = BaseTrainingSchema.extend({
    type: z.literal('course'),
    id: z.string(),
    courseId: z.number().int(),
    title: z.string(),
    shortDescription: z.string(),
    description: z.string(),
    image: AcademyImageSchema.nullable(),
    badges: z.array(AcademyBadgeSchema),
    points: z.number().int().nonnegative().nullable(),
    status: DoceboCourseStatusSchema.nullable(),
    durationMinutes: z.number().int().nonnegative().nullable(),
    certificateUrl: z.string().nullable()
});

export type RequireFields<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

export type AcademyBadge = z.infer<typeof AcademyBadgeSchema>;
export type BaseTraining = z.infer<typeof BaseTrainingSchema>;
export type TrainingListingEvent = z.infer<typeof TrainingListingEventSchema>;
export type TrainingCourseDetail = z.infer<typeof TrainingCourseDetailSchema>;
export type TrainingsListingItem = TrainingCourseDetail | TrainingListingEvent;

export type TeaserDetailsItem = Pick<
    BaseTraining,
    'type' | 'durationMinutes' | 'points' | 'hasCertificate' | 'startsAt' | 'location'
>;
